import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    InputSignal,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    signal,
    untracked,
    viewChild,
    AfterViewInit,
    OnDestroy,
} from '@angular/core';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { z } from 'zod';
import { IqGraphLtStateService } from '../state';
import { setUpGraph, updateGraph } from '../state/services/iq-graph-lt-utils';

const GraphTrucksPayloadSchema = z.object({
    id: z.string(),
    hasError: z.boolean().default(false),
    loaded: z.boolean().default(false),
    trucks: z.array(
        z.object({
            date: z.string(),
            data: z.number(),
        }),
    ),
});

type GraphTrucksPayload = z.infer<typeof GraphTrucksPayloadSchema>;

@Component({
    selector: 'iq-graph-lt-trucks',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './iq-graph-lt-trucks.component.html',
    styleUrl: './iq-graph-lt-trucks.component.scss',
})
export class IqGraphLtTrucksComponent implements AfterViewInit, OnDestroy {
    id: InputSignal<string> = input('');
    graph: Signal<ElementRef> = viewChild<ElementRef>('graph');

    private _trucksPayload: WritableSignal<GraphTrucksPayload> = signal({});

    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _deregister: Record<string, IqEventBusBrokerHandler> = {};

    loaded: Signal<boolean> = computed(() => {
        return this._trucksPayload().loaded;
    });
    chartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.DateAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    };

    constructor(private _state: IqGraphLtStateService) {
        this._deregister = this._bus.registerMap({
            updateLtGraphTrucks: {
                callback: (payload: GraphTrucksPayload) => {
                    if (payload.id !== this.id()) return;

                    const test: any =
                        GraphTrucksPayloadSchema.safeParse(payload);

                    if (!test.success) {
                        console.warn(
                            'GraphTrucksPayloadSchema Invalid Payload',
                        );
                        console.log(test?.error?.issues);
                        return;
                    }

                    this._trucksPayload.set(payload);
                },
            },
        });
        effect(() => {
            const loaded = this.loaded();
            const data = this._trucksPayload().trucks;

            if (!loaded) return;

            untracked(async () => {
                await updateGraph(this.chartInfo, data, this.truckProps);
            });
        });
    }

    private truckProps = {
        strokeColor: '#0097AF',
    };

    ngAfterViewInit(): void {
        this.chartInfo = setUpGraph(this.graph());
    }

    ngOnDestroy(): void {
        this._bus.unregisterMap(this._deregister);
    }
}
